/*
 * -----------------------------------------------------------------------
 *  <copyright file="central-monitoring.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql, TypedDocumentNode } from '@apollo/client';
import {
    PanelFragment,
    PanelListFragment,
    FieldDefinitionFragment,
    CentralMonitoringObservationAndActionFragment,
    CentralMonitoringActionFragment,
    DashboardFragment,
    DataSetFragment,
    DataSetReviewFragment,
    DataReviewTableFragment,
    DataReviewTableAllColumnsFragment,
    ExtraDataFieldFragment,
    DataLoadFragment,
    RowDataSourceFragment,
    PanelForObservationFragment,
    DataViewConfigurationFragment,
    DataViewFilterFragment,
    StudyDashboardConfigFragment,
    StudyDashboardConfigWidgetFragment,
    StudyDashboardConfigPanelFragment,
    DataViewConfigResultsFragment,
    DataViewConfigNamesFragment,
    DataViewFilterResultsFragment,
    getAllWidgetsForStudyFragment,
    getStudyHubStudyFieldsConfigFragment,
    getStudyHubSiteFieldsConfigFragment,
    StudyHubMonitoringToolkitConfigFragment,
    ControlsHubConfigWidgetFragment,
    getAllControlsHubWidgetsForStudyFragment,
    ControlsHubConfigPanelFragment, ControlsHubConfigPanelResolvedFragment,
    IntegrationConfigMetadataFragment, getStudyHubPanelLineSelectorConfigFragment, IntegrationFieldsAndCreatedFragment,
} from './fragments';
import {
    getSubjectHubConfigFields,
    getSubjectHubConfigFieldsVariables,
    saveSubjectHubConfigFields,
    saveSubjectHubConfigFieldsVariables,
} from '@queries/generated/central-monitoring.generated';

export const GET_PANELS = gql`
    query getPanels($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        panels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelListFields
        }
    }
    ${PanelListFragment}
`;

export const GET_CM2_PANELS = gql`
    query getCm2Panels($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        cm2panels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelListFields
        }
    }
    ${PanelListFragment}
`;

export const GET_CM2_PANELS_DATAVIEW = gql`
    query getCm2PanelsDataview($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        cm2panels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelFields
        }
    }
    ${PanelFragment}
`;

export const GET_FIELDS_AND_DATA_SOURCES = gql`
    query getFieldsAndDataSources($studyId: ObjectId!, $timestamp: DateTime) {
        fields(studyId: $studyId, timestamp: $timestamp) {
            ...FieldDefinitionFields
        },
        rowDataSources(studyId: $studyId, timestamp: $timestamp) {
            ...RowDataSourceFields   
        }
    }
    ${FieldDefinitionFragment}
    ${RowDataSourceFragment}
`;

export const GET_PANELS_FOR_OBSERVATION = gql`
    query getPanelsForObservation($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        panels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelForObservationFields
        }
    }
    ${PanelForObservationFragment}
`;

export const GET_PANEL = gql`
    query panel($panelId: ObjectId!, $timestamp: DateTime) {
        panel(panelId: $panelId, timestamp: $timestamp) {
            ...PanelFields
        }
    }
    ${PanelFragment}
`;

export const GET_CM2_PANEL = gql`
    query cm2Panel($panelId: ObjectId!, $timestamp: DateTime) {
        cm2Panel(panelId: $panelId, timestamp: $timestamp) {
            ...PanelFields
        }
    }
    ${PanelFragment}
`;

export const SAVE_PANEL = gql`
    mutation upsertPanel($panel: PanelInput!, $reason: String!) {
        upsertPanel(panel: $panel, reason: $reason) {
            ...PanelFields
        }
    }
    ${PanelFragment}
`;

export const EXECUTE_PANEL = gql`
    query executePanel($panel: PanelInput!) {
        executePanel(panel: $panel)
    }    
`;

export const GET_CM_OBSERVATIONS_AND_ACTIONS = gql`
    query centralMonitoringObservations($studyId: ObjectId!, $siteId: ObjectId, $datasetId: ObjectId, $timestamp: DateTime, $includeDeleted: Boolean = true) {
        centralMonitoringObservations(studyId: $studyId, siteId: $siteId, datasetId: $datasetId, timestamp: $timestamp, includeDeleted: $includeDeleted) {
            ...CentralMonitoringObservationAndActionFields
        }
    }
    ${CentralMonitoringObservationAndActionFragment}
`;

export const SAVE_CM_OBSERVATION = gql`
    mutation saveCentralMonitoringObservation($observation: CentralMonitoringObservationInput!) {
        saveCentralMonitoringObservation(observation: $observation) {
            ...CentralMonitoringObservationAndActionFields
        }
    }
    ${CentralMonitoringObservationAndActionFragment}
`;

export const DELETE_CM_OBSERVATION = gql`
    mutation deleteCentralMonitoringObservation($id: ObjectId!, $reason: String) {
        deleteCentralMonitoringObservation(id: $id, reason: $reason)
    }
`;

export const RESTORE_CM_OBSERVATION = gql`
    mutation unDeleteCentralMonitoringObservation($id: ObjectId!, $unDeleteActions: Boolean = false) {
        unDeleteCentralMonitoringObservation(id: $id, unDeleteActions: $unDeleteActions)
    }
`;

export const DELETE_CM_OBSERVATION_ACTION = gql`
    mutation deleteCentralMonitoringObservationAction($actionId: ObjectId!, $observationId: ObjectId!, $reason: String) {
        deleteCentralMonitoringObservationAction(actionId: $actionId, observationId: $observationId, reason: $reason)
    }
`;

export const RESTORE_CM_OBSERVATION_ACTION = gql`
    mutation unDeleteCentralMonitoringObservationAction($actionId: ObjectId!, $observationId: ObjectId!) {
        unDeleteCentralMonitoringObservationAction(actionId: $actionId, observationId: $observationId)
    }
`;

export const SAVE_CM_ACTION = gql`
    mutation saveCentralMonitoringObservationAction($observationId: ObjectId!, $action: CentralMonitoringObservationActionInput!) {
        saveCentralMonitoringObservationAction(observationId: $observationId, action: $action) {
            ...CentralMonitoringActionFields
        }
    }
    ${CentralMonitoringActionFragment}
`;

export const SEND_CM_ACTION_EMAIL = gql`
    mutation sendCentralMonitoringActionsEmail($cc: String, $notes: String, $observationIds: [ObjectId]!, $actionIds: [ObjectId]!) {
        sendCentralMonitoringActionsEmail(cc: $cc, notes: $notes, observationIds: $observationIds, actionIds: $actionIds)
    }
`;

export const GET_DASHBOARDS = gql`
    query dashboards($studyId: ObjectId!, $timestamp: DateTime) {
        dashboards(studyId: $studyId, timestamp: $timestamp) {
            ...DashboardFields
        }
    }
    ${DashboardFragment}
`;

export const SAVE_DASHBOARD = gql`
    mutation upsertDashboard($dashboard: DashboardInput!, $reason: String!) {
        upsertDashboard(dashboard: $dashboard, reason: $reason) {
            ...DashboardFields
        }
    }
    ${DashboardFragment}
`;

export const GET_CM_REVIEW_REPORT_DATA = gql`
    query centralMonitoringReviewData($studyId: ObjectId!, $dataSetId: ObjectId, $onlyReviewed: Boolean) {
        centralMonitoringReviewData(studyId: $studyId, dataSetId: $dataSetId, onlyReviewed: $onlyReviewed) {   
            siteStatusesToExclude
            studySummary {
                studyNum
                sponsor
                screenedSubjects
                enrolledSubjects
                studyDiscontinuedSubjects
                treatmentDiscontinuedSubjects
                completedSubjects
                screenFailedSubjects
                randomisedSubjects
                showScreenedSubjects
                showEnrolledSubjects
                showStudyDiscontinuedSubjects
                showTreatmentDiscontinuedSubjects
                showCompletedSubjects
                showScreenFailedSubjects
                showRandomisedSubjects
            }
            studyReview {
                reviewer
                reviewCompleteDate
                reviewStartDate
                reviewSummary
                reviewed
                recordId
                updated
                updatedBy
            }
            riskOverviews {
                site
                siteId
                numRedKris
                numAmberKris
                numRedDecisionKris
                numAmberDecisionKris
                numEnrolledSubjects
                numScreenedSubjects
                numSubjects
                numDiscontinuedSubjects
                score
                previousScore
                previousColour                
                siteActiveDate
                siteCountry
                siteMonitor
                siteStatus
                colour                
                kriScores {
                    name
                    label
                    change
                    colour
                    panelId
                    score
                    average
                }
            }
            riskOverviewColumns {
                panelId
                text
                includeInSiteScoring
                weight
            }
            riskOverviewConfiguration {
                subjectField
                amberKris
                redKris
                amberDecisionKris
                redDecisionKris
                siteStatus
                siteActiveDate
                siteCountry
                siteMonitor
                numSubjects
            }
            studySites {
                recordId
                updated
                updatedBy
                site
                siteId
                screenedSubjects
                enrolledSubjects
                discontinuedSubjects
                activationDate
                reviewer
                reviewed
                reviewStartDate
                reviewSummary
                siteScore
                siteScoreChange
                siteScoreColour
                siteStatus
                kriScores {
                    name
                    label
                    change
                    colour
                    score
                    average
                }
                observations {
                    id
                    owner
                    createdBy
                    createdDate
                    recentAction
                    openActions
                    observationDate
                    panelName
                    dataSetDate
                    closedDate
                    updatedDate
                    updatedBy
                    observation
                    potentialCause
                    confirmedCause      
                    comments
                    actions {
                        id
                        owner
                        updatedDate
                        updatedBy                        
                        completedDate
                        requestDate
                        expectedCompleteDate
                        description
                        comments
                        result
                        actionTaken
                        rootCause
                    }
                }
            }
        }
    }  
`;

export const GET_STUDY_HAS_DATASETS = gql`
    query studyHasDataSets($studyId: ObjectId!){
        studyHasDataSets(studyId: $studyId)
    }
`;

export const GET_DATASETS = gql`
    query dataSets($studyId: ObjectId!) {
        dataSets(studyId: $studyId) {
            ...DataSetFields
        }
    }
    ${DataSetFragment}
`;

export const SAVE_DATASET = gql`
    mutation saveDataSet($id: ObjectId!, $active: Boolean!, $notes: String) {
        saveDataSet(id: $id, active: $active, notes: $notes) {
            ...DataSetFields
        }
    }
    ${DataSetFragment}
`;
export const GET_DATASET_REVIEWS = gql`
    query dataSetReviews($studyId: ObjectId!, $excludeReportReviews: Boolean) {
        dataSetReviews(studyId: $studyId, excludeReportReviews: $excludeReportReviews) {
            ...DataSetReviewFields
        }
    }
    ${DataSetReviewFragment}    
`;

export const GET_DATA_REVIEW_TABLES = gql`
    query dataReviewTables($studyId: ObjectId!, $timestamp: DateTime) {
        dataReviewTablesForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...DataReviewTableFields
        }
    }
    ${DataReviewTableFragment}
`;

// AllColumns walks the table's full version history (more expensive than Columns), so it's kept
// out of DataReviewTableFragment and only queried for the one table actually being edited, e.g.
// in the Review Plan edit dialog where a stale "on watch" column needs to be flagged and labelled.
export const GET_DATA_REVIEW_TABLE_ALL_COLUMNS = gql`
    query dataReviewTableAllColumns($id: ObjectId!, $timestamp: DateTime) {
        dataReviewTable(id: $id, timestamp: $timestamp) {
            id
            allColumns {
                ...DataReviewTableAllColumnsFields
            }
        }
    }
    ${DataReviewTableAllColumnsFragment}
`;

// Bulk variant of the above, for callers that need every study table's full column history at
// once - e.g. the Review Plan audit log, which resolves column names across however many
// listings a given audit change touched (a column since removed from its table still needs its
// last known heading, rather than falling back to its raw field key).
export const GET_DATA_REVIEW_TABLES_ALL_COLUMNS = gql`
    query dataReviewTablesAllColumns($studyId: ObjectId!, $timestamp: DateTime) {
        dataReviewTablesForStudy(studyId: $studyId, timestamp: $timestamp) {
            id
            allColumns {
                ...DataReviewTableAllColumnsFields
            }
        }
    }
    ${DataReviewTableAllColumnsFragment}
`;

export const SAVE_DATA_REVIEW_TABLE = gql`
    mutation saveDataReviewTable($table: DataReviewTableInput!, $reason: String!) {
        upsertDataReviewTable(dataReviewTable: $table, reason: $reason) {
            ...DataReviewTableFields
        }
    }
    ${DataReviewTableFragment}
`;

export const PREVIEW_DATA_REVIEW_TABLE = gql`
    query previewDataReviewTable($table: DataReviewTableInput!, $timestamp: DateTime) {
        previewDataReviewTable(table: $table, timestamp: $timestamp)
    }
`;

export const EXECUTE_DATA_REVIEW_TABLE = gql`
    query executeDataReviewTable($id: ObjectId!, $studyId: ObjectId!, $dataSetId: ObjectId) {
        executeDataReviewTable(id: $id, studyId: $studyId, dataSetId: $dataSetId)
    }
`;

export const GET_EXTRA_FIELDS = gql`
    query extraDataFields($studyId: ObjectId!) {
        extraDataFields(studyId: $studyId) {
            ...ExtraDataFields
        }
    }  
    ${ExtraDataFieldFragment}
`;

export const SAVE_EXTRA_FIELD = gql`
    mutation saveExtraField($field: ExtraDataFieldInput!, $reason: String!) {
        upsertExtraDataField(extraDataField: $field, reason: $reason) {
            ...ExtraDataFields
        }
    }
    ${ExtraDataFieldFragment}
`;

export const GET_IMPORTS_FOR_STUDY = gql`
    query getImportsForStudy($studyId: ObjectId!) {
        importsForStudy(studyId: $studyId) {
            ...DataLoadFields
        }
    }
    ${DataLoadFragment}
`;

export const GET_IMPORT_LOGS = gql`
    query getImportLogs($logIds: [ObjectId]!) {
        logsForImport(logIds: $logIds) {
            id
            created
            importedType
            addedCount
            modifiedCount
            sizeIncrease
            warnings
            errors
            log
        }
    }  
`;

export const SAVE_DATASET_REVIEW = gql`
    mutation saveDataSetReview(
        $dataSetId: ObjectId!, 
        $siteId: ObjectId, 
        $reviewed: Boolean!, 
        $summary: String!, 
        $startDate: Date, 
        $completedDate: Date, 
        $reviewer: String) 
    {
        saveDataSetReview(
            dataSetId: $dataSetId, 
            siteId: $siteId, 
            reviewed: $reviewed, 
            summary: $summary, 
            startDate: $startDate, 
            completedDate: $completedDate, 
            reviewer: $reviewer) 
        {
            ...DataSetReviewFields
        }
    }
    ${DataSetReviewFragment}
`;

export const GET_DATA_VIEW_CONFIGURATIONS = gql`
    query dataViewConfigurations($studyId: ObjectId!, $timestamp: DateTime) {
        dataViewConfigurations(studyId: $studyId, timestamp: $timestamp) {
            ...DataViewConfigurationFields
        }
    }
    ${DataViewConfigurationFragment}
`;

export const GET_DATA_VIEW_CONFIGURATION = gql`
    query dataViewConfiguration($studyId: ObjectId!, $configId: ObjectId!, $timestamp: DateTime!) {
        dataViewConfiguration(studyId: $studyId, configId: $configId, timestamp: $timestamp) {
            ...DataViewConfigurationFields
        }
    }
    ${DataViewConfigurationFragment}
`;

export const GET_DATA_VIEW_CONFIGURATION_NAMES = gql`
    query dataViewConfigurationNames($studyId: ObjectId!) {
        dataViewConfigurations(studyId: $studyId) {
            ...DataViewConfigNames
        }
    } 
    ${DataViewConfigNamesFragment}
`;

export const GET_DATA_VIEW_DATA_FILTERS = gql`
    query dataViewDataFilters($studyId: ObjectId!) {
        dataViewDataFilters(studyId: $studyId) {
            ...DataViewFilterFields
        }
    }
    ${DataViewFilterFragment}
`;
export const GET_DATA_VIEW_SITE_AND_SUBJECT_FILTERS = gql`
    query dataViewSiteAndSubjectFilters($studyId: ObjectId!) {
        dataViewSiteAndSubjectFilters(studyId: $studyId) {
            ...DataViewFilterFields
        }
    }
    ${DataViewFilterFragment}
`;


export const GET_STUDY_DASHBOARD_CONFIGURATION = gql`
    query studyDashboardConfigForStudy($studyId: ObjectId!) {
        studyDashboardConfigForStudy(studyId: $studyId) {
            ...StudyDashboardConfigurationFields
        }
    }
    ${StudyDashboardConfigFragment}
`;

export const ADD_STUDY_DASHBOARD_CONFIG = gql`
    mutation addStudyDashboardConfig($studyId: ObjectId!, $studyDashboardConfig: StudyDashboardConfigurationInput!) {
        addStudyDashboardConfig(studyId: $studyId, studyDashboardConfig: $studyDashboardConfig) {
            ...StudyDashboardConfigurationFields
        }
    }
    ${StudyDashboardConfigFragment}
`;

export const GET_STUDY_AUTOMATION_CONFIG = gql`
    query getStudyAutomationConfig($studyId: ObjectId!, $timestamp: DateTime) {
        getStudyAutomationConfig(studyId: $studyId, timestamp: $timestamp) {
            studyAutomationColour,
            excludedSiteStatuses,
            isAutomationEnabled,
            isControlsAutomationEnabled,
            controlsAutomationColor
            
        }
    }
`;

export const SAVE_STUDY_AUTOMATION_CONFIG = gql`
    mutation addStudyAutomationConfig($studyId: ObjectId!, $excludedSiteStatuses: [String!]!) {
        addStudyAutomationConfig(
            studyId: $studyId,
            excludedSiteStatuses: $excludedSiteStatuses
        )
    }
`;

export const GET_STUDY_DASHBOARD_CONFIGURATION_WIDGET = gql`
    query getStudyDashboardConfigWidgetsForStudy($studyId: ObjectId!, $timestamp: DateTime) {
        getStudyDashboardConfigWidgetsForStudy(studyId: $studyId, timestamp: $timestamp)  {
            ...StudyDashboardConfigurationWidgetFields
        }
    }
    ${StudyDashboardConfigWidgetFragment}
`;

export const GET_SUBJECT_HUB_CONFIG_FIELDS: TypedDocumentNode<
    getSubjectHubConfigFields,
    getSubjectHubConfigFieldsVariables
> = gql`
    query getSubjectHubConfigFields($studyId: ObjectId!) {
        getSubjectHubConfigFields(studyId: $studyId) {
            key
            name
            type
            label
            displayIndex
        }
    }
`;
export const SAVE_SUBJECT_HUB_CONFIG_FIELDS: TypedDocumentNode<
    saveSubjectHubConfigFields,
    saveSubjectHubConfigFieldsVariables
> = gql`
    mutation saveSubjectHubConfigFields($fields: [SubjectHubConfigField]!, $studyId: ObjectId!) {
        saveSubjectHubConfigFields(fields: $fields, studyId: $studyId )
    }
`;

export const ADD_STUDY_DASHBOARD_CONFIG_WIDGET = gql`
    mutation addStudyDashboardConfigWidget($studyId: ObjectId!, $widgets: [StudyDashboardConfigWidgetInputType]! ) {
        addStudyDashboardConfigWidget(studyId: $studyId, widgets: $widgets) {
            ...StudyDashboardConfigurationWidgetFields
        }
    }
    ${StudyDashboardConfigWidgetFragment}
`;

export const GET_STUDY_DASHBOARD_CONFIG_PANELS = gql`
    query getStudyDashboardConfigPanelsForStudy($studyId: ObjectId!, $timestamp: DateTime) {
        getStudyDashboardConfigPanelsForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...StudyDashboardConfigurationPanelFields
        }
    }
    ${StudyDashboardConfigPanelFragment}
`;

export const ADD_STUDY_DASHBOARD_CONFIG_PANELS = gql`
    mutation addStudyDashboardConfigPanels($panelRefs: [String]!, $studyId: ObjectId!) {
        addStudyDashboardConfigPanels(panelRefs: $panelRefs, studyId: $studyId ) {
            ...StudyDashboardConfigurationPanelFields
        }
    }
    ${StudyDashboardConfigPanelFragment}
`;

export const SAVE_DATA_VIEW_CONFIGURATION = gql`
    mutation addDataViewConfig($dataSource: RowDataSourceTable!, $isActive: Boolean!, $mainPanelId: ObjectId!, $name: String!, $studyId: ObjectId!) {
        addDataViewConfig(dataSource: $dataSource, isActive: $isActive, mainPanelId: $mainPanelId, name: $name, studyId: $studyId) {
            ...DataViewConfigResults
        }
    }
    ${DataViewConfigResultsFragment}
`;
export const SAVE_DATA_VIEW_DISPLAY_ORDER = gql`
    mutation updateDataViewDisplayOrder($configIds: [ObjectId]!, $studyId: ObjectId!) {
        updateDataViewDisplayOrder(configIds: $configIds, studyId: $studyId )      
    }
    
`;

export const DELETE_DATA_VIEW = gql`
    mutation deleteDataViewConfiguration($configId: ObjectId!, $reason: String) {
        deleteDataViewConfiguration(configId: $configId, reason: $reason )
    }
`;

export const UPDATE_DATA_VIEW = gql`
    mutation updateDataViewConfiguration($config: DataViewConfigurationInput!, $reason: String) {
        updateDataViewConfiguration(config: $config, reason: $reason) {
            ...DataViewConfigResults
        }
    }
    ${DataViewConfigResultsFragment}
`;
export const ADD_DATA_FILTER = gql`
    mutation addDataFilter($dataSource: RowDataSourceTable!,  $fieldKey: String!, $fieldName: String!, $label: String!, $studyId: ObjectId!) {
        addDataFilter(dataSource: $dataSource,  fieldKey: $fieldKey, fieldName: $fieldName, label: $label, studyId: $studyId) {
            ...DataViewFilterResults
        }
    }
    ${DataViewFilterResultsFragment}
`;
export const UPDATE_DATA_FILTER = gql`
    mutation updateDataFilter($filter: DataViewFilterInput!) {
        updateDataFilter(filter: $filter) {
            ...DataViewFilterResults
        }
    }
    ${DataViewFilterResultsFragment}
`;

export const GET_STUDY_DASHBOARD_WIDGETS = gql`
    query getAllWidgetsForStudy($studyId: ObjectId!, $timestamp: DateTime) {
        getAllWidgetsForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...getAllWidgetsForStudyFields
        }
    }
    ${getAllWidgetsForStudyFragment}
`;

export const GET_CONTROLS_HUB_WIDGETS = gql`
    query getAllControlsHubWidgetsForStudy($studyId: ObjectId!, $timestamp: DateTime) {
        getAllControlsHubWidgetsForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...getAllControlsHubWidgetsForStudyFields
        }
    }
    ${getAllControlsHubWidgetsForStudyFragment}
`;

export const ADD_SITE_AND_SUBJECT_DATA_FILTER = gql`
    mutation addSiteAndSubjectFilter($dataSource: RowDataSourceTable!,  $fieldKey: String!, $fieldName: String!, $label: String!, $studyId: ObjectId!) {
        addSiteAndSubjectFilter(dataSource: $dataSource,  fieldKey: $fieldKey, fieldName: $fieldName, label: $label, studyId: $studyId) {
            ...DataViewFilterResults
        }
    }
    ${DataViewFilterResultsFragment}
`;
export const UPDATE_SITE_AND_SUBJECT_DATA_FILTER = gql`
    mutation updateSiteAndSubjectFilter($filter: DataViewFilterInput!) {
        updateSiteAndSubjectFilter(filter: $filter) {
            ...DataViewFilterResults
        }
    }
    ${DataViewFilterResultsFragment}
`;
export const SAVE_SITE_AND_SUBJECT_DISPLAY_ORDER = gql`
    mutation updateSiteAndSubjectFilterDiplayOrder($filterIds: [ObjectId]!, $studyId: ObjectId!, $dataSource: RowDataSourceTable!) {
        updateSiteAndSubjectFilterDisplayOrder(filterIds: $filterIds, studyId: $studyId, dataSource: $dataSource )
    }
`;
export const DELETE_SITE_AND_SUBJECT_FILTER = gql`
    mutation deleteSiteAndSubjectFilter($filterId: ObjectId!, $studyId: ObjectId!, $dataSource: RowDataSourceTable!) {
        deleteSiteAndSubjectFilter(filterId: $filterId, studyId: $studyId, dataSource: $dataSource )
    }
`;

export const GET_DATA_VIEW_ACTIVE_CONFIGURATION_NAMES = gql`
    query datasetDataViewConfigurations($studyId: ObjectId!, $timestamp: DateTime!) {
            dataViewConfigurations(studyId: $studyId, timestamp: $timestamp) {
            ...DataViewConfigNames
        }
    }
    ${DataViewConfigNamesFragment}
`;

export const GET_STUDY_HUB_STUDY_FIELDS_CONFIG = gql`
    query getStudyHubStudyFieldsConfig($studyId: ObjectId!) {
        getStudyHubStudyFieldsConfig(studyId: $studyId ) {
            ...studyHubStudyFieldsConfigFields
        }
    }
    ${getStudyHubStudyFieldsConfigFragment}
`;

export const GET_STUDY_HUB_PANEL_LINE_SELECTOR_CONFIG = gql`
    query getStudyHubPanelLineSelectorConfig($studyId: ObjectId!) {
        getStudyHubPanelLineSelectorConfig(studyId: $studyId ) {
            ...studyHubPanelLineSelectorConfigFields
        }
    }
    ${getStudyHubPanelLineSelectorConfigFragment}
`;

export const ADD_STUDY_HUB_STUDY_FIELDS_CONFIG = gql`
    mutation upsertStudyHubStudyFieldsConfig($studyId: ObjectId!, $studyHubStudyFieldsConfig: StudyHubStudyFieldsConfigInput!) {
        upsertStudyHubStudyFieldsConfig(studyId: $studyId, studyHubStudyFieldsConfig: $studyHubStudyFieldsConfig) {
            ...studyHubStudyFieldsConfigFields
        }
    }
    ${getStudyHubStudyFieldsConfigFragment}
`;


export const ADD_STUDY_HUB_PANEL_LINE_SELECTOR_CONFIG = gql`
    mutation upsertStudyHubPanelLineSelectorConfig($studyId: ObjectId!, $studyHubPanelLineSelectorConfig: StudyHubPanelLineSelectorConfigInput!) {
        upsertStudyHubPanelLineSelectorConfig(studyId: $studyId, studyHubPanelLineSelectorConfig: $studyHubPanelLineSelectorConfig) {
            ...studyHubPanelLineSelectorConfigFields
        }
    }
    ${getStudyHubPanelLineSelectorConfigFragment}
`;

export const GET_STUDY_HUB_SITE_FIELDS_CONFIG = gql`
    query getStudyHubSiteFieldsConfig($studyId: ObjectId!) {
        getStudyHubSiteFieldsConfig(studyId: $studyId ) {
            ...studyHubSiteFieldsConfigFields
        }
    }
    ${getStudyHubSiteFieldsConfigFragment}
`;

export const ADD_STUDY_HUB_SITE_FIELDS_CONFIG = gql`
    mutation upsertStudyHubSiteFieldsConfig($studyId: ObjectId!, $studyHubSiteFieldsConfig: StudyHubSiteFieldsConfigInput!) {
        upsertStudyHubSiteFieldsConfig(studyId: $studyId, studyHubSiteFieldsConfig: $studyHubSiteFieldsConfig) {
            ...studyHubSiteFieldsConfigFields
        }
    }
    ${getStudyHubSiteFieldsConfigFragment}
`;

export const GET_STUDY_HUB_MONITORING_TOOLKIT_CONFIGURATION = gql`
    query getMonitoringToolkitConfig($studyId: ObjectId!) {
        getMonitoringToolkitConfig(studyId: $studyId) {
            ...StudyHubMonitoringToolkitConfigFields
        }
    }
    ${StudyHubMonitoringToolkitConfigFragment}
`;

export const UPDATE_STUDY_HUB_MONITORING_TOOLKIT_CONFIG = gql`
    mutation updateMonitoringToolkitConfig($studyId: ObjectId!, $monitoringToolkitConfig: StudyHubMonitoringToolkitConfigInput!) {
        updateMonitoringToolkitConfig(studyId: $studyId, monitoringToolkitConfig: $monitoringToolkitConfig) {
            ...StudyHubMonitoringToolkitConfigFields
        }
    }
    ${StudyHubMonitoringToolkitConfigFragment}
`;

export const GET_CONTROLS_HUB_WIDGET_CONFIG = gql`
    query getControlsHubConfigWidgetsForStudy($studyId: ObjectId!, $timestamp: DateTime) {
        getControlsHubConfigWidgetsForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...ControlsHubConfigWidgetFields
        }
    }
    ${ControlsHubConfigWidgetFragment}
`;

export const ADD_CONTROLS_HUB_CONFIG_WIDGET = gql`
    mutation addControlsHubConfigWidget($studyId: ObjectId!, $widgets: [ControlsHubConfigWidgetInputType]! ) {
        addControlsHubConfigWidget(studyId: $studyId, widgets: $widgets) {
            ...ControlsHubConfigWidgetFields
        }
    }
    ${ControlsHubConfigWidgetFragment}
`;

export const GET_CONTROLS_HUB_CONFIG_PANELS = gql`
    query getControlsHubConfigPanelsForStudy($studyId: ObjectId!, $timestamp: DateTime) {
        getControlsHubConfigPanelsForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...ControlsHubConfigurationPanelFields
        }
    }
    ${ControlsHubConfigPanelFragment}
`;

export const GET_CONTROLS_HUB_CONFIG_PANELS_RESOLVED = gql`
    query getControlsHubConfigPanelsForStudyResolved($studyId: ObjectId!, $timestamp: DateTime) {
        getControlsHubConfigPanelsResolvedForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...ControlsHubConfigurationPanelResolvedFields
        }
    }
    ${ControlsHubConfigPanelResolvedFragment}
`;


export const ADD_CONTROLS_HUB_CONFIG_PANELS = gql`
    mutation addControlsHubConfigPanels($panels: [PanelAndDataViewInput]!, $studyId: ObjectId!) {
        addControlsHubConfigPanels(panels: $panels, studyId: $studyId ) {
            ...ControlsHubConfigurationPanelFields
        }
    }
    ${ControlsHubConfigPanelFragment}
`;

export const GET_INTEGRATION_CONFIGS = gql`
    query getIntegrationConfigs {
        integrationConfigs {
            ...IntegrationConfigMetadataFields
        }
    }
    ${IntegrationConfigMetadataFragment}
`;
export const GET_INTEGRATION_FIELDS_AND_CREATED = gql`
    query getIntegrationFieldsAndCreated($studyId: ObjectId!) {
        integrationFieldsAndCreated(studyId: $studyId) {
            ...IntegrationFieldsAndCreatedFields
        }
    }
    ${IntegrationFieldsAndCreatedFragment}
`;
